import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification, NotificationFilters, UnreadCount } from '@/types/notifications';
import {
  NOTIFICATION_TYPE_LABELS,
  NOTIFICATION_TYPE_ICONS,
} from '@/types/notifications';
import {
  getMyNotifications,
  markAsRead,
  markAllAsRead,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  getRealtimeSubscriptionStatus,
} from '@/services/notifications';
import { mockNotifications } from '@/mocks/notificationCenter';
import { SwalAlert } from '@/hooks/useSweetAlert';

// ─── Filters ────────────────────────────────────────────────

type FilterId = 'all' | 'unread' | 'urgent' | 'high' | 'normal';

const filtersList: { key: FilterId; label: string; icon: string }[] = [
  { key: 'all', label: 'Todas', icon: 'ri-notification-3-line' },
  { key: 'unread', label: 'No leídas', icon: 'ri-mail-unread-line' },
  { key: 'urgent', label: 'Urgentes', icon: 'ri-error-warning-line' },
  { key: 'high', label: 'Alta prioridad', icon: 'ri-arrow-up-circle-line' },
  { key: 'normal', label: 'Normal', icon: 'ri-checkbox-circle-line' },
];

// ─── Helpers ─────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTypeColor(type: string): string {
  const map: Record<string, string> = {
    PROGRAM_STARTING: 'bg-sky-100 text-sky-700',
    RADIO_LIVE: 'bg-red-100 text-red-700',
    ANNOUNCER_ON_AIR: 'bg-violet-100 text-violet-700',
    REQUEST_ACCEPTED: 'bg-emerald-100 text-emerald-700',
    REQUEST_REJECTED: 'bg-rose-100 text-rose-700',
    REQUEST_ATTENDED: 'bg-teal-100 text-teal-700',
    REQUEST_PLAYED: 'bg-indigo-100 text-indigo-700',
    NEW_PODCAST: 'bg-amber-100 text-amber-700',
    CONTEST_STARTED: 'bg-orange-100 text-orange-700',
    CONTEST_ENDING: 'bg-yellow-100 text-yellow-700',
    CONTEST_WINNER: 'bg-pink-100 text-pink-700',
    RADIO_ANNOUNCEMENT: 'bg-cyan-100 text-cyan-700',
    SYSTEM_NOTICE: 'bg-foreground-100 text-foreground-600',
  };
  return map[type] || 'bg-foreground-100 text-foreground-600';
}

function getPriorityColor(priority: string): string {
  const map: Record<string, string> = {
    urgent: 'bg-red-100 text-red-700',
    high: 'bg-accent-100 text-accent-700',
    normal: 'bg-foreground-100 text-foreground-600',
  };
  return map[priority] || 'bg-foreground-100 text-foreground-600';
}

// ─── Component ──────────────────────────────────────────────

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailNotif, setDetailNotif] = useState<Notification | null>(null);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCount>({ total: 0, urgent: 0, high: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [usingMock, setUsingMock] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState<string>('unsubscribed');
  const realtimeCleanupRef = useRef<(() => void) | null>(null);

  // ── Load data ──────────────────────────────────────────

  const loadData = useCallback(async (pageNum: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const filters: NotificationFilters = {
        page: pageNum,
        per_page: 20,
        sort: 'newest',
      };
      const result = await getMyNotifications(filters);

      if (result.notifications.length > 0) {
        setItems(result.notifications);
        setTotalPages(Math.ceil(result.total / result.per_page));
        setUnreadCounts({
          total: result.unread_count,
          urgent: result.notifications.filter((n) => !n.is_read && n.priority === 'urgent').length,
          high: result.notifications.filter((n) => !n.is_read && n.priority === 'high').length,
        });
        setUsingMock(false);
      } else {
        // Fallback a mock
        setItems(mockNotifications);
        setTotalPages(1);
        const unread = mockNotifications.filter((n) => !n.is_read);
        setUnreadCounts({
          total: unread.length,
          urgent: unread.filter((n) => n.priority === 'urgent').length,
          high: unread.filter((n) => n.priority === 'high').length,
        });
        setUsingMock(true);
      }
    } catch {
      // Fallback a mock en caso de error
      setItems(mockNotifications);
      setTotalPages(1);
      const unread = mockNotifications.filter((n) => !n.is_read);
      setUnreadCounts({
        total: unread.length,
        urgent: unread.filter((n) => n.priority === 'urgent').length,
        high: unread.filter((n) => n.priority === 'high').length,
      });
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Realtime ───────────────────────────────────────────

  useEffect(() => {
    // Subscribe for realtime new notifications
    const cleanup = subscribeToNotifications((notif: Notification) => {
      setItems((prev) => [notif, ...prev]);
      setUnreadCounts((prev) => ({
        ...prev,
        total: prev.total + 1,
        urgent: prev.urgent + (notif.priority === 'urgent' ? 1 : 0),
        high: prev.high + (notif.priority === 'high' ? 1 : 0),
      }));
    });

    realtimeCleanupRef.current = cleanup;

    // Poll realtime status periodically
    const statusInterval = setInterval(() => {
      setRealtimeStatus(getRealtimeSubscriptionStatus());
    }, 2000);

    return () => {
      cleanup();
      clearInterval(statusInterval);
    };
  }, []);

  useEffect(() => {
    loadData(page);
  }, [loadData, page]);

  // ── Filtering ──────────────────────────────────────────

  const filtered = useMemo(() => {
    switch (activeFilter) {
      case 'unread':
        return items.filter((n) => !n.is_read);
      case 'urgent':
        return items.filter((n) => n.priority === 'urgent');
      case 'high':
        return items.filter((n) => n.priority === 'high');
      case 'normal':
        return items.filter((n) => n.priority === 'normal');
      default:
        return items;
    }
  }, [items, activeFilter]);

  const unreadCount = items.filter((n) => !n.is_read).length;

  // ── Actions ────────────────────────────────────────────

  const handleClickNotif = async (notif: Notification) => {
    setDetailNotif(notif);
    if (!notif.is_read) {
      try {
        if (!usingMock) await markAsRead(notif.id);
      } catch {
        // mock fallback — seguir
      }
      setItems((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)),
      );
      setUnreadCounts((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        urgent: Math.max(0, prev.urgent - (notif.priority === 'urgent' ? 1 : 0)),
        high: Math.max(0, prev.high - (notif.priority === 'high' ? 1 : 0)),
      }));
    }
  };

  const handleNavigateToEntity = (notif: Notification) => {
    if (notif.action_url) {
      navigate(notif.action_url);
      setDetailNotif(null);
    }
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) {
      SwalAlert.warning('Sin notificaciones nuevas', 'Todas las notificaciones ya están leídas.');
      return;
    }
    const result = await SwalAlert.confirm(
      'Marcar todas como leídas',
      `Tenés ${unreadCount} notificación(es) sin leer. ¿Querés marcarlas todas como leídas?`,
    );
    if (result.isConfirmed) {
      try {
        if (!usingMock) await markAllAsRead();
      } catch {
        // fallback
      }
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCounts({ total: 0, urgent: 0, high: 0 });
      SwalAlert.toast(`${unreadCount} marcadas como leídas`, 'success');
    }
  };

  const handleDelete = (id: string) => {
    const notif = items.find((n) => n.id === id);
    if (!notif) return;
    SwalAlert.confirm(
      '¿Eliminar notificación?',
      `Se eliminará "${notif.title}".`,
    ).then((result) => {
      if (result.isConfirmed) {
        setItems((prev) => prev.filter((n) => n.id !== id));
        if (detailNotif?.id === id) setDetailNotif(null);
        if (!notif.is_read) {
          setUnreadCounts((prev) => ({
            ...prev,
            total: Math.max(0, prev.total - 1),
            urgent: Math.max(0, prev.urgent - (notif.priority === 'urgent' ? 1 : 0)),
            high: Math.max(0, prev.high - (notif.priority === 'high' ? 1 : 0)),
          }));
        }
        SwalAlert.toast('Notificación eliminada', 'success');
      }
    });
  };

  const clearAll = () => {
    if (items.length === 0) return;
    SwalAlert.confirm(
      '¿Limpiar todas?',
      `Se eliminarán las ${items.length} notificación(es). Esta acción no se puede deshacer.`,
    ).then((result) => {
      if (result.isConfirmed) {
        setItems([]);
        setDetailNotif(null);
        setUnreadCounts({ total: 0, urgent: 0, high: 0 });
        SwalAlert.toast('Todas las notificaciones fueron eliminadas', 'success');
      }
    });
  };

  // ── Render ─────────────────────────────────────────────

  const hasUrgent = filtered.some((n) => n.priority === 'urgent' && !n.is_read);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold text-foreground-950">Centro de Notificaciones</h2>
          {unreadCount > 0 && (
            <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-[11px] font-bold rounded-full">
              {unreadCount} sin leer
            </span>
          )}
          {hasUrgent && (
            <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[10px] font-bold whitespace-nowrap flex items-center gap-1">
              <i className="ri-error-warning-line text-xs"></i>Urgente
            </span>
          )}
          {realtimeStatus === 'subscribed' && (
            <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold whitespace-nowrap flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              En vivo
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              Marcar todas leídas
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={clearAll}
              className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              Limpiar todas
            </button>
          )}
        </div>
      </div>

      {/* Unread summary */}
      {unreadCounts.total > 0 && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className="text-xs text-foreground-500">
            {unreadCounts.urgent > 0 && (
              <span className="text-red-600 font-semibold">{unreadCounts.urgent} urgentes</span>
            )}
            {unreadCounts.urgent > 0 && unreadCounts.high > 0 && <span className="text-foreground-300"> · </span>}
            {unreadCounts.high > 0 && (
              <span className="text-accent-600 font-semibold">{unreadCounts.high} alta prioridad</span>
            )}
            {unreadCounts.high > 0 && unreadCounts.total - unreadCounts.urgent - unreadCounts.high > 0 && <span className="text-foreground-300"> · </span>}
            {unreadCounts.total - unreadCounts.urgent - unreadCounts.high > 0 && (
              <span className="text-foreground-600">{unreadCounts.total - unreadCounts.urgent - unreadCounts.high} normales</span>
            )}
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto no-scrollbar">
        {filtersList.map((f) => {
          const isActive = activeFilter === f.key;
          let count: number | null = null;
          if (f.key === 'unread') count = items.filter((n) => !n.is_read).length;
          else if (f.key === 'urgent') count = items.filter((n) => n.priority === 'urgent').length;
          else if (f.key === 'high') count = items.filter((n) => n.priority === 'high').length;
          else if (f.key === 'normal') count = items.filter((n) => n.priority === 'normal').length;

          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${f.icon} text-sm`}></i>
              {f.label}
              {count !== null && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-background-50/20 text-background-50' : 'bg-foreground-200 text-foreground-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* LOADING */}
      {loading && (
        <div className="space-y-1.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-background-50 rounded-xl p-4 animate-pulse">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-background-200 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-28 rounded-md bg-background-200"></div>
                    <div className="h-3 w-12 rounded-md bg-background-200 ml-auto"></div>
                  </div>
                  <div className="h-3 w-full rounded-md bg-background-200"></div>
                  <div className="h-3 w-2/3 rounded-md bg-background-200"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ERROR */}
      {!loading && error && (
        <div className="text-center py-16 bg-background-50 rounded-2xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <h3 className="text-foreground-800 font-bold text-sm mb-1">Error al cargar</h3>
          <p className="text-foreground-400 text-xs max-w-xs mx-auto">{error}</p>
          <button
            onClick={() => loadData(page)}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            Reintentar
          </button>
        </div>
      )}

      {/* EMPTY */}
      {!loading && !error && filtered.length === 0 && (
        <div className="bg-background-50 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-notification-off-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm font-medium">No hay notificaciones</p>
          <p className="text-foreground-400 text-xs mt-1">
            {activeFilter === 'unread'
              ? 'Estás al día. Todas las notificaciones fueron leídas.'
              : activeFilter !== 'all'
                ? `No tenés notificaciones de tipo "${activeFilter}".`
                : 'Aquí aparecerán tus avisos y alertas.'}
          </p>
          {activeFilter !== 'all' && (
            <button
              onClick={() => setActiveFilter('all')}
              className="mt-3 px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-200 rounded-lg cursor-pointer transition-colors"
            >
              Ver todas
            </button>
          )}
        </div>
      )}

      {/* LIST */}
      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="space-y-1.5">
            {filtered.map((notif) => (
              <div
                key={notif.id}
                className={`relative bg-background-50 rounded-xl px-4 py-3.5 flex items-start gap-3.5 cursor-pointer hover:bg-background-100/80 transition-all group ${
                  !notif.is_read ? 'ring-1 ring-primary-200/60' : ''
                }`}
                onClick={() => handleClickNotif(notif)}
              >
                {/* Unread dot */}
                {!notif.is_read && (
                  <div className="absolute top-4 left-3 w-2 h-2 bg-primary-500 rounded-full"></div>
                )}

                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ml-1 ${getTypeColor(notif.type)}`}>
                  <i className={`${NOTIFICATION_TYPE_ICONS[notif.type] || 'ri-notification-line'} text-base`}></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`text-sm ${!notif.is_read ? 'font-bold text-foreground-950' : 'font-semibold text-foreground-800'}`}>
                      {notif.title}
                    </h4>
                    {notif.priority === 'urgent' && (
                      <span className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 text-[9px] font-bold whitespace-nowrap">URGENTE</span>
                    )}
                    {notif.priority === 'high' && (
                      <span className="px-1.5 py-0.5 rounded-md bg-accent-100 text-accent-700 text-[9px] font-bold whitespace-nowrap">Alta</span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{notif.message}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[10px] text-foreground-400">{formatTime(notif.created_at)}</span>
                    <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                    <span className="text-[10px] text-foreground-500">{NOTIFICATION_TYPE_LABELS[notif.type]}</span>
                    {notif.metadata && (notif.metadata as Record<string, unknown>).radio_name && (
                      <>
                        <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                        <span className="text-[10px] text-foreground-500">
                          {(notif.metadata as Record<string, unknown>).radio_name as string}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right side */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(notif.id); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-all shrink-0 opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-100 hover:bg-background-200 rounded-lg cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
              >
                Anterior
              </button>
              <span className="text-xs text-foreground-500">
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-100 hover:bg-background-200 rounded-lg cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

      {/* DETAIL MODAL */}
      {detailNotif && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetailNotif(null)}
        >
          <div
            className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${getTypeColor(detailNotif.type)}`}>
                  <i className={`${NOTIFICATION_TYPE_ICONS[detailNotif.type] || 'ri-notification-line'} text-base`}></i>
                </div>
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">Detalle de notificación</h3>
                  <p className="text-foreground-400 text-[10px]">{formatFullDate(detailNotif.created_at)}</p>
                </div>
              </div>
              <button
                onClick={() => setDetailNotif(null)}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${getTypeColor(detailNotif.type)}`}>
                  {NOTIFICATION_TYPE_LABELS[detailNotif.type]}
                </span>
                {detailNotif.priority !== 'normal' && (
                  <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-bold ${getPriorityColor(detailNotif.priority)}`}>
                    {detailNotif.priority === 'urgent' ? 'URGENTE' : 'Alta prioridad'}
                  </span>
                )}
              </div>
              <h3 className="text-foreground-950 font-bold text-base">{detailNotif.title}</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">{detailNotif.message}</p>

              {/* Entity link section */}
              {detailNotif.related_entity_type && (
                <div className="bg-background-100 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground-800 text-xs font-semibold">
                        {detailNotif.related_entity_type === 'radio' && 'Radio'}
                        {detailNotif.related_entity_type === 'program' && 'Programa'}
                        {detailNotif.related_entity_type === 'announcer' && 'Locutor'}
                        {detailNotif.related_entity_type === 'podcast' && 'Podcast'}
                        {detailNotif.related_entity_type === 'contest' && 'Concurso'}
                        {detailNotif.related_entity_type === 'request' && 'Solicitud'}
                      </p>
                      <p className="text-foreground-400 text-[10px]">
                        {(detailNotif.metadata as Record<string, unknown>)?.radio_name as string || detailNotif.related_entity_id}
                      </p>
                    </div>
                    {detailNotif.action_url && (
                      <button
                        onClick={() => handleNavigateToEntity(detailNotif)}
                        className="px-3 py-1.5 text-xs font-semibold bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 cursor-pointer whitespace-nowrap transition-colors"
                      >
                        Ir
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-background-100 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Estado</span>
                  <span className={detailNotif.is_read ? 'text-foreground-500' : 'text-accent-600 font-semibold'}>
                    {detailNotif.is_read ? 'Leída' : 'No leída'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Recibida</span>
                  <span className="text-foreground-600">{formatFullDate(detailNotif.created_at)}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Prioridad</span>
                  <span className={`${getPriorityColor(detailNotif.priority)} text-[10px] px-2 py-0.5 rounded-full font-semibold`}>
                    {detailNotif.priority === 'urgent' ? 'Urgente' : detailNotif.priority === 'high' ? 'Alta' : 'Normal'}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-background-200 shrink-0 gap-2">
              <div className="flex items-center gap-2">
                {detailNotif.action_url && (
                  <button
                    onClick={() => handleNavigateToEntity(detailNotif)}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line text-sm"></i>
                    Ir
                  </button>
                )}
                {!detailNotif.is_read && (
                  <button
                    onClick={async () => {
                      try {
                        if (!usingMock) await markAsRead(detailNotif.id);
                      } catch { /* mock */ }
                      setItems((prev) => prev.map((n) => (n.id === detailNotif.id ? { ...n, is_read: true } : n)));
                      setUnreadCounts((prev) => ({
                        ...prev,
                        total: Math.max(0, prev.total - 1),
                        urgent: Math.max(0, prev.urgent - (detailNotif.priority === 'urgent' ? 1 : 0)),
                        high: Math.max(0, prev.high - (detailNotif.priority === 'high' ? 1 : 0)),
                      }));
                      SwalAlert.toast('Marcada como leída', 'success');
                    }}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-primary-50 text-primary-600 text-xs font-semibold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-check-line text-sm"></i>
                    Marcar leída
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  handleDelete(detailNotif.id);
                  setDetailNotif(null);
                }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line text-sm"></i>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}